import SEO from '../../components/SEO';
import React from 'react';
import { motion } from 'framer-motion';
import { Download, Play, CheckCircle2, Target, TrendingUp, BarChart3, Shield, Zap, Eye, DollarSign, Award } from 'lucide-react';
import { Button } from '../../components/ui/button';
import ProductCTA from '../../components/ProductCTA';
import PersonaNavigation from '../../components/PersonaNavigation';

const ManagingDirectorPage = () => {
  const stats = [
    { value: "360", label: "Business Visibility" },
    { value: "Real-time", label: "KPI Dashboards" },
    { value: "100%", label: "Data Accuracy" },
    { value: "Mobile", label: "Access Anywhere" }
  ];

  const challenges = [
    { title: "Limited Strategic Visibility", desc: "Relying on month-old reports instead of real-time business intelligence" },
    { title: "Departmental Silos", desc: "Finance, sales, and operations working in isolated systems" },
    { title: "Growth Constraints", desc: "Current systems cannot scale with acquisitions or expansion" },
    { title: "Risk Blindness", desc: "Unable to identify operational risks early enough" }
  ];

  const benefits = [
    { icon: Eye, title: "Complete Business Visibility", strapline: "360 degree real-time overview", desc: "Single executive dashboard showing profit status, operational efficiency, and risk signals across all departments instantly." },
    { icon: TrendingUp, title: "Strategic Growth Insights", strapline: "Data-driven expansion", desc: "Identify profitable segments, regions to expand, and market opportunities with predictive analytics." },
    { icon: BarChart3, title: "KPI-Driven Management", strapline: "Monitor what matters", desc: "Track customer acquisition costs, cash flow, and sales performance with customisable dashboards." },
    { icon: Shield, title: "Risk Mitigation", strapline: "Early warning signals", desc: "Detect profit risks, compliance issues, and operational bottlenecks before they impact business." },
    { icon: Zap, title: "Agile Decision-Making", strapline: "Act on live data", desc: "Make confident decisions based on real-time data with mobile access anywhere." },
    { icon: DollarSign, title: "Scalable Growth", strapline: "Expand without limits", desc: "Scale seamlessly with acquisitions or international expansion without replacing your ERP Solution." }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="SAP Business One for Managing Directors | Strategic Business Intelligence"
        description="Complete business intelligence for MDs. Real-time dashboards, KPI tracking, strategic insights. Make confident decisions with SAP Business One data visibility."
        keywords="MD business intelligence, CEO dashboard, strategic ERP, business visibility, SAP B1 for directors"
        canonical="https://www.ascarii.com/sap-business-one/for/managing-directors"
      />

      <section className="relative min-h-[700px] flex items-center overflow-hidden bg-gradient-to-br from-slate-800 via-blue-900 to-slate-900">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1920&q=85" alt="Executive" className="w-full h-full object-cover opacity-30" loading="eager" />
        </div>
        <div className="container mx-auto px-4 lg:px-8 py-20 relative z-10">
          <div className="max-w-4xl">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full text-sm font-semibold mb-6 text-white">For Managing Directors</div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight text-white">Lead with Complete Business Intelligence</h1>
              <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed">Make strategic decisions with confidence using real-time visibility, KPI dashboards, and predictive analytics.</p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.1 }} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">{stat.value}</div>
                <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Persona Navigation */}
      <PersonaNavigation currentPersona="managing-directors" />

      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} className="relative group flex">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl w-full">
                <img src="https://images.unsplash.com/photo-1503551723145-6c040742065b-v2?w=800&q=85" alt="Business Challenges" className="w-full h-full object-cover" loading="eager" />
                <div className="absolute inset-0 bg-gradient-to-tr from-red-600/30 to-transparent" />
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="flex flex-col justify-center">
              <div className="inline-block px-4 py-2 bg-red-100 text-red-700 rounded-full text-sm font-semibold mb-4 w-fit">Strategic Challenges</div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Problems Holding Your Business Back</h2>
              <div className="space-y-4">
                {challenges.map((c, i) => (
                  <div key={i} className="flex items-start gap-4 p-4 bg-white rounded-xl border border-red-100">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-red-600 font-bold">-</span>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">{c.title}</h3>
                      <p className="text-sm text-gray-600">{c.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="flex flex-col justify-center">
              <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4 w-fit">Management by Exception</div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Focus on What Matters Most</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                By receiving more accurate business performance data quicker, you adopt a <strong>Management by Exception position</strong> where you are notified only of things requiring your attention.
              </p>
              <div className="p-6 bg-gradient-to-br from-blue-50 to-white rounded-xl border border-blue-200 mb-4">
                <h3 className="font-bold text-gray-900 mb-3">Informed Strategic Decisions</h3>
                <p className="text-gray-700">
                  With improved processes, better reporting, and accurate KPIs, you make more informed and timely strategic decisions to ensure the business attains your growth and profitability aspirations.
                </p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="relative group flex">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl w-full">
                <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=85" alt="Dashboard" className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-tl from-blue-600/20 to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-slate-800 via-blue-900 to-slate-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Why Managing Directors Choose SAP Business One</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {benefits.map((benefit, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.05 }} viewport={{ once: true }} className="group p-8 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 hover:border-cyan-300 hover:bg-white/20 transition-all">
                <div className="flex items-start gap-6">
                  <div className="w-20 h-20 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-10 h-10 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-2">{benefit.title}</h3>
                    <p className="text-lg text-cyan-300 mb-3 font-medium">{benefit.strapline}</p>
                    <p className="text-blue-100 text-sm leading-relaxed">{benefit.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-12 border-2 border-blue-200">
              <div className="text-center">
                <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-6">Proven Success</div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Abbeydale Direct</h2>
                <p className="text-lg text-gray-700 italic mb-4">
                  Ascarii works with us as though they are an extension of our team. Ascarii and SAP have taken our business from small thinking to making all things not only possible, but a reality.
                </p>
                <p className="text-sm text-gray-600">- Managing Director, Abbeydale Direct</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-gray-100 via-blue-50 to-gray-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 items-start">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="lg:col-span-2">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Transform Your Business Leadership</h2>
              <p className="text-xl text-gray-600 mb-8">Join MDs running data-driven, agile businesses</p>
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/10 to-cyan-500/10 flex items-center justify-center">
                    <Target className="w-7 h-7 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Strategic Control</h4>
                    <p className="text-gray-600 text-sm">360 degree visibility</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/10 to-cyan-500/10 flex items-center justify-center">
                    <Award className="w-7 h-7 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Proven Platform</h4>
                    <p className="text-gray-600 text-sm">80,000+ SMEs worldwide</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/10 to-cyan-500/10 flex items-center justify-center">
                    <Zap className="w-7 h-7 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Mobile Access</h4>
                    <p className="text-gray-600 text-sm">Manage from anywhere</p>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="lg:col-span-3">
              <ProductCTA productName="SAP Business One for Managing Directors" brandColor="blue-600" />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ManagingDirectorPage;
